import React from 'react';
import { Link } from 'react-router-dom';
import '../styles/RecipeCard.css';

const RecipeCard = ({ recipe, variant = 'minimal' }) => {
    // Function to get cuisine type badge class
    const getCuisineBadgeClass = (category) => {
        if (!category) return 'badge-default';
        return `badge-${category.toLowerCase()}`;
    };

    // Function to format cooking time
    const formatCookingTime = (minutes) => {
        if (!minutes) return 'No time specified';
        if (minutes < 60) return `${minutes}m`;
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    };

    // Parse ingredients if they're stored as a string
    const ingredients = typeof recipe.ingredients === 'string' 
        ? JSON.parse(recipe.ingredients) 
        : recipe.ingredients || [];

    // Get short description from recipe description or generate one
    const getShortDescription = () => {
        if (recipe.shortDescription) return recipe.shortDescription;
        if (recipe.description) {
            const firstSentence = recipe.description.split('. ')[0];
            return firstSentence.length > 80 ? firstSentence.substring(0, 80) + '…' : firstSentence + '…';
        }
        return `A delicious ${recipe.category?.toLowerCase() || 'recipe'} that takes ${formatCookingTime(recipe.cooking_time)} to prepare.`;
    };

    // Determine navigation state based on variant
    const linkState = variant === 'minimal' ? { from: 'explore' } : { from: 'my-recipes' };

    return (
        <Link 
            to={`/recipe/${recipe.id}`} 
            className={`recipe-card ${variant}`}
            state={linkState}
        >
            {/* Image container with square aspect ratio */}
            <div className="recipe-card-image-container">
                {recipe.images && recipe.images.length > 0 ? (
                    <img 
                        src={recipe.images[0]} 
                        alt={recipe.name}
                        className="card-image"
                    />
                ) : (
                    <div className="recipe-card-placeholder">
                        <span className="placeholder-icon">◐</span>
                    </div>
                )}
            </div>
            
            <div className="card-body">
                <h3 className="card-title">{recipe.name}</h3>
                
                <div className="card-meta">
                    <div className="card-time-author">
                        <span className="card-time">
                            {formatCookingTime(recipe.cooking_time)}
                        </span>
                        <span className="card-author">
                            By {recipe.creator_name || 'Unknown User'}
                        </span>
                    </div>
                    {recipe.forker_name && (
                        <span className="card-forker">
                            Forked by {recipe.forker_name}
                        </span>
                    )}
                    {recipe.category && (
                        <span className={`card-category ${getCuisineBadgeClass(recipe.category)}`}>
                            {recipe.category}
                        </span>
                    )}
                </div>

                {/* Vibes tags at the bottom */}
                <div className="card-vibes-container">
                    <div className="card-vibes">
                        {recipe.vibes && recipe.vibes.map((vibe, index) => (
                            <span key={index} className="vibe-tag">{vibe}</span>
                        ))}
                        {/* If no vibes, add some default ones based on category */}
                        {(!recipe.vibes || recipe.vibes.length === 0) && recipe.category && (
                            <>
                                <span className="vibe-tag">{recipe.category.toLowerCase()}</span>
                                {recipe.cooking_time && recipe.cooking_time <= 30 && (
                                    <span className="vibe-tag">quick</span>
                                )}
                                {recipe.cooking_time && recipe.cooking_time > 60 && (
                                    <span className="vibe-tag">slow-cooked</span>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </Link>
    );
};

export default RecipeCard;